// Base Store Module Template
// Provides consistent patterns for all store modules

export const createBaseModule = (name, apiService, options = {}) => {
  const {
    // Custom state properties
    customState = {},
    // Custom getters
    customGetters = {},
    // Custom mutations  
    customMutations = {},
    // Custom actions
    customActions = {},
    // ID field name (default: 'id')
    idField = 'id'
  } = options

  return {
    namespaced: true,

    state: {
      // Standard state properties
      items: [],
      currentItem: null,
      loading: false,
      error: null,
      
      // Pagination support
      totalCount: 0,
      currentPage: 1,
      itemsPerPage: 10,
      
      // Search/filter support
      searchQuery: '',
      filters: {},
      
      // Custom state properties
      ...customState
    },

    getters: {
      // Standard getters
      all: state => state.items,
      getById: state => id => state.items.find(item => item[idField] === id),
      current: state => state.currentItem,
      isLoading: state => state.loading,
      error: state => state.error,
      hasError: state => !!state.error,
      
      // Pagination getters
      totalCount: state => state.totalCount,
      currentPage: state => state.currentPage,
      itemsPerPage: state => state.itemsPerPage,
      totalPages: state => Math.ceil(state.totalCount / state.itemsPerPage),
      
      // Search/filter getters
      searchQuery: state => state.searchQuery,
      filters: state => state.filters,
      hasActiveFilters: state => Object.keys(state.filters).length > 0,
      
      // Filtered items
      filteredItems: state => {
        let items = state.items
        
        // Apply search query
        if (state.searchQuery) {
          const query = state.searchQuery.toLowerCase()
          items = items.filter(item => 
            JSON.stringify(item).toLowerCase().includes(query)
          )
        }
        
        // Apply filters
        Object.entries(state.filters).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            items = items.filter(item => {
              if (Array.isArray(value)) {
                return value.includes(item[key])
              }
              return item[key] === value
            })
          }
        })
        
        return items
      },
      
      // Custom getters
      ...customGetters
    },

    mutations: {
      // Standard mutations
      SET_LOADING(state, loading) {
        state.loading = loading
      },
      
      SET_ERROR(state, error) {
        state.error = error
        state.loading = false
      },
      
      CLEAR_ERROR(state) {
        state.error = null
      },
      
      SET_ITEMS(state, items) {
        state.items = Array.isArray(items) ? items : []
        state.loading = false
        state.error = null
      },
      
      SET_CURRENT_ITEM(state, item) {
        state.currentItem = item
        state.loading = false
        state.error = null
      },
      
      ADD_ITEM(state, item) {
        if (item && item[idField]) {
          // Check if item already exists
          const existingIndex = state.items.findIndex(existing => 
            existing[idField] === item[idField]
          )
          
          if (existingIndex >= 0) {
            // Update existing item
            state.items.splice(existingIndex, 1, item)
          } else {
            // Add new item
            state.items.push(item)
          }
        }
        state.loading = false
        state.error = null
      },
      
      UPDATE_ITEM(state, { id, updates }) {
        const index = state.items.findIndex(item => item[idField] === id)
        if (index >= 0) {
          state.items.splice(index, 1, { ...state.items[index], ...updates })
        }
        
        // Update current item if it's the same
        if (state.currentItem && state.currentItem[idField] === id) {
          state.currentItem = { ...state.currentItem, ...updates }
        }
        
        state.loading = false
        state.error = null
      },
      
      REMOVE_ITEM(state, id) {
        state.items = state.items.filter(item => item[idField] !== id)
        
        // Clear current item if it's the one being removed
        if (state.currentItem && state.currentItem[idField] === id) {
          state.currentItem = null
        }
        
        state.loading = false
        state.error = null
      },
      
      // Pagination mutations
      SET_PAGINATION(state, { totalCount, currentPage, itemsPerPage }) {
        if (totalCount !== undefined) state.totalCount = totalCount
        if (currentPage !== undefined) state.currentPage = currentPage
        if (itemsPerPage !== undefined) state.itemsPerPage = itemsPerPage
      },
      
      // Search/filter mutations
      SET_SEARCH_QUERY(state, query) {
        state.searchQuery = query || ''
      },
      
      SET_FILTER(state, { key, value }) {
        if (value === null || value === undefined || value === '') {
          delete state.filters[key]
        } else {
          state.filters = { ...state.filters, [key]: value }
        }
      },
      
      CLEAR_FILTERS(state) {
        state.filters = {}
        state.searchQuery = ''
      },
      
      // Custom mutations
      ...customMutations
    },

    actions: {
      // Standard CRUD actions
      async fetchAll({ commit }, params = {}) {
        try {
          commit('SET_LOADING', true)
          commit('CLEAR_ERROR')
          
          const data = await apiService.getAll(params)
          commit('SET_ITEMS', data)
          
          return data
        } catch (error) {
          commit('SET_ERROR', error.message || `Failed to fetch ${name}`)
          throw error
        }
      },

      async fetchById({ commit }, id) {
        try {
          commit('SET_LOADING', true)
          commit('CLEAR_ERROR')
          
          const data = await apiService.getById(id)
          commit('SET_CURRENT_ITEM', data)
          
          return data
        } catch (error) {
          commit('SET_ERROR', error.message || `Failed to fetch ${name}`)
          throw error
        }
      },

      async create({ commit, dispatch }, itemData) {
        try {
          commit('SET_LOADING', true)
          commit('CLEAR_ERROR')
          
          const data = await apiService.create(itemData)
          commit('ADD_ITEM', data)
          
          // Show success message
          dispatch('showSuccess', `${name} created successfully`, { root: true })
          
          return data
        } catch (error) {
          commit('SET_ERROR', error.message || `Failed to create ${name}`)
          dispatch('handleError', error, { root: true })
          throw error
        }
      },

      async update({ commit, dispatch }, { id, updates }) {
        try {
          commit('SET_LOADING', true)
          commit('CLEAR_ERROR')
          
          const data = await apiService.update(id, updates)
          commit('UPDATE_ITEM', { id, updates: data })
          
          // Show success message
          dispatch('showSuccess', `${name} updated successfully`, { root: true })
          
          return data
        } catch (error) {
          commit('SET_ERROR', error.message || `Failed to update ${name}`)
          dispatch('handleError', error, { root: true })
          throw error
        }
      },

      async delete({ commit, dispatch }, id) {
        try {
          commit('SET_LOADING', true)
          commit('CLEAR_ERROR')
          
          await apiService.delete(id)
          commit('REMOVE_ITEM', id)
          
          // Show success message
          dispatch('showSuccess', `${name} deleted successfully`, { root: true })
          
          return true
        } catch (error) {
          commit('SET_ERROR', error.message || `Failed to delete ${name}`)
          dispatch('handleError', error, { root: true })
          throw error
        }
      },

      // Utility actions
      clearError({ commit }) {
        commit('CLEAR_ERROR')
      },

      setCurrentItem({ commit }, item) {
        commit('SET_CURRENT_ITEM', item)
      },

      clearCurrentItem({ commit }) {
        commit('SET_CURRENT_ITEM', null)
      },

      // Search and filter actions
      setSearchQuery({ commit }, query) {
        commit('SET_SEARCH_QUERY', query)
      },

      setFilter({ commit }, { key, value }) {
        commit('SET_FILTER', { key, value })
      },

      clearFilters({ commit }) {
        commit('CLEAR_FILTERS')
      },

      // Pagination actions
      setPagination({ commit }, paginationData) {
        commit('SET_PAGINATION', paginationData)
      },

      // Custom actions
      ...customActions
    }
  }
}
